import type { VehicleInsurance } from "@credit-acceptance/step6-bff-client";
import { z } from "zod";

// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const REQUIRED_MESSAGE = "This is a required field.";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const POLICY_NUMBER_LENGTH_MESSAGE = "Must be 100 characters or fewer";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const AGENT_NAME_LENGTH_MESSAGE = "Must be 40 characters or fewer";
// eslint-disable-next-line i18n-text/no-en -- hardcoded validation copy, matching Signers.schema.ts's convention
const DEDUCTIBLE_MAX_MESSAGE = "Must be 1000 or less";

/** A fully-zeroed phone number is the legacy placeholder — treated the same as blank. */
const BLANK_PHONE_PLACEHOLDER = "000-000-0000";

// ---------------------------------------------------------------------------
// insuranceInformationSchema — RHF + Zod for InsuranceInformation.tsx, same
// dual-mode TextField/CurrencyField/DateField/PhoneField/LossPayeeField/
// FullCoverageField pattern as ContractVehicleInformation.schema.ts. Amount
// Financed stays LabeledValue-driven, outside this schema (it's sourced from
// `financial.amountFinanced`, not `VehicleInsurance`).
// ---------------------------------------------------------------------------
export const FULL_COVERAGE_VALUES = ["Y", "N"] as const;
export type FullCoverageValue = (typeof FULL_COVERAGE_VALUES)[number];

export const POLICY_NUMBER_MAX_LENGTH = 100;
export const AGENT_NAME_MAX_LENGTH = 40;
export const DEDUCTIBLE_MAX = 1000;

export const insuranceInformationSchema = z
  .object({
    // Mapped from the boolean VehicleInsurance.fullCoverage; undefined hides the field. Never validated itself.
    fullCoverage: z.enum(FULL_COVERAGE_VALUES).optional(),
    policyNumber: z.string().max(POLICY_NUMBER_MAX_LENGTH, POLICY_NUMBER_LENGTH_MESSAGE),
    insurerName: z.string().min(1, REQUIRED_MESSAGE),
    // Required check (blank or the placeholder mask) lives in the superRefine below —
    // the placeholder-as-blank rule isn't expressible as a plain string check here.
    insurerPhone: z.string(),
    agentName: z.string().max(AGENT_NAME_MAX_LENGTH, AGENT_NAME_LENGTH_MESSAGE),
    // No hard requirement enforced — matches the legacy "cosmetic-only, doesn't block save" behavior.
    expirationDate: z.string(),
    // Nullable (not just optional) — react-hook-form's `useController` cannot
    // reactively clear a field to `undefined` once it's had a real default (a
    // sibling `setValue`/`field.onChange(undefined)` silently falls back to
    // the original default instead), but `null` propagates correctly. `null`
    // is Full Coverage's clear-on-toggle sentinel; both mean "blank" below.
    comprehensiveDeductibleAmount: z.number().nullable().optional(),
    collisionDeductibleAmount: z.number().nullable().optional(),
    // Never validated — just maps DB codes Y/N/U to a display value.
    isCaLossPayee: z.enum(["Y", "N", "U"]).optional(),
  })
  .superRefine((data, context) => {
    if (!data.insurerPhone || data.insurerPhone === BLANK_PHONE_PLACEHOLDER) {
      context.addIssue({ code: "custom", path: ["insurerPhone"], message: REQUIRED_MESSAGE });
    }

    if (data.fullCoverage !== "Y") {
      return;
    }
    // eslint-disable-next-line unicorn/no-null -- `null` (not `undefined`) is Full Coverage's clear-on-toggle sentinel, see the field's comment above
    if (data.comprehensiveDeductibleAmount == null) {
      context.addIssue({ code: "custom", path: ["comprehensiveDeductibleAmount"], message: REQUIRED_MESSAGE });
    } else if (data.comprehensiveDeductibleAmount > DEDUCTIBLE_MAX) {
      context.addIssue({
        code: "custom",
        path: ["comprehensiveDeductibleAmount"],
        message: DEDUCTIBLE_MAX_MESSAGE,
      });
    }
    // eslint-disable-next-line unicorn/no-null -- see the comprehensiveDeductibleAmount check above
    if (data.collisionDeductibleAmount == null) {
      context.addIssue({ code: "custom", path: ["collisionDeductibleAmount"], message: REQUIRED_MESSAGE });
    } else if (data.collisionDeductibleAmount > DEDUCTIBLE_MAX) {
      context.addIssue({ code: "custom", path: ["collisionDeductibleAmount"], message: DEDUCTIBLE_MAX_MESSAGE });
    }
  });

export type InsuranceInformationInput = z.infer<typeof insuranceInformationSchema>;

/**
 * Builds RHF defaultValues from the real `VehicleInsurance` prop.
 * @param insurance The dashboard response's `VehicleInsurance`, if any.
 * @returns {InsuranceInformationInput} Seed values for `useForm`'s `defaultValues`.
 */
export function buildInsuranceDefaultValues(insurance?: VehicleInsurance): InsuranceInformationInput {
  let fullCoverage: FullCoverageValue | undefined;
  if (insurance?.fullCoverage !== undefined) {
    fullCoverage = insurance.fullCoverage ? "Y" : "N";
  }
  return {
    fullCoverage,
    policyNumber: insurance?.policyNumber ?? "",
    insurerName: insurance?.insurerName ?? "",
    insurerPhone: insurance?.insurerPhone ?? "",
    agentName: insurance?.agentName ?? "",
    expirationDate: insurance?.expirationDate ?? "",
    // eslint-disable-next-line unicorn/no-null -- `null` is the blank sentinel for these fields, see the schema above
    comprehensiveDeductibleAmount: insurance?.comprehensiveDeductibleAmount ?? null,
    // eslint-disable-next-line unicorn/no-null -- see above
    collisionDeductibleAmount: insurance?.collisionDeductibleAmount ?? null,
    isCaLossPayee: insurance?.isCaLossPayee ?? "U",
  };
}
